/* global BigInt */
import { INVOICE_IMPORT_FIELDS, invoiceImportCoordinateVersions, requiredInvoiceImportFields } from "../invoiceImport";

const fieldMap = new Map(INVOICE_IMPORT_FIELDS.map(field => [field.key, field]));
const string = value => typeof value === "string" ? value : value == null ? "" : String(value);
const bounded = (value, length = 240) => string(value).slice(0, length);
const object = value => value && typeof value === "object" && !Array.isArray(value) ? value : {};
const invoiceType = value => string(value).toLowerCase().replace(/\s+/g, "_");
const date = value => {
  if (!/^[1-9]\d{3}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};
const units = value => {
  if (!/^(?:0|[1-9]\d{0,13})(?:\.\d{1,4})?$/.test(value)) return null;
  const [whole, fraction = ""] = value.split(".");
  return BigInt(whole) * 10000n + BigInt(fraction.padEnd(4, "0"));
};
const locationKey = location => {
  const source = object(location);
  return [source.page ?? null, source.line ?? null, source.x ?? null, source.y ?? null,
    source.width ?? null, source.height ?? null, bounded(source.kind),
    (Array.isArray(source.token_ids) ? source.token_ids : []).slice(0, 1200).map(id => bounded(id, 100))];
};
const assignmentKey = assignment => {
  const source = object(assignment);
  return JSON.stringify([bounded(source.method), source.confidence ?? null, bounded(source.sourceText),
    (Array.isArray(source.tokenIds) ? source.tokenIds : []).slice(0, 1200).map(id => bounded(id, 100)),
    locationKey(source.sourceLocation)]);
};

// This is a UI draft binding, not an authorization token or a financial receipt.
// Keep the actual source and extraction separate from the mutable intake revision.
export function invoiceReviewSourceKey(item = {}) {
  const extraction = object(item.extraction?.invoice || item.extraction);
  const tokens = Array.isArray(extraction.tokens) ? extraction.tokens.slice(0, 1200) : [];
  return JSON.stringify([
    bounded(item.workspaceId, 100), bounded(item.businessId, 100), bounded(item.id, 100),
    bounded(item.sourceId, 100), bounded(item.fileHash, 100), invoiceType(item.documentType),
    bounded(item.extraction?.id || extraction.id, 100), bounded(extraction.parserVersion, 100),
    INVOICE_IMPORT_FIELDS.map(field => {
      const source = object(extraction.fields?.[field.key]);
      return [field.key, bounded(source.value), assignmentKey(source)];
    }),
    tokens.map(token => [bounded(token.id, 100), token.page, token.line, token.x, token.y,
      token.width, token.height, bounded(token.text)]),
  ]);
}

const labelFor = (key, documentType) => key === "supplier" && invoiceType(documentType) === "sales_invoice"
  ? "Buyer or customer" : fieldMap.get(key)?.label || key;
const issue = (field, code, message) => ({ field, code, message });

function fieldIssue(field, values, required, documentType) {
  const value = string(values[field.key]).trim(), label = labelFor(field.key, documentType);
  if (!value) return required ? issue(field.key, "required_missing", `Enter ${label.toLowerCase()}.`) : null;
  // Source text must not introduce hidden control characters into invoice fields.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f]/.test(value) || value.length > (field.key === "supplier" ? 200 : 160)) {
    return issue(field.key, "invalid_text", `Use plain text within ${field.key === "supplier" ? 200 : 160} characters for ${label.toLowerCase()}.`);
  }
  if (field.key === "supplier" && value.length < 2) return issue(field.key, "invalid_party", `Enter the full ${label.toLowerCase()} name.`);
  if (field.type === "date" && !date(value)) return issue(field.key, "invalid_date", `Enter a valid ${label.toLowerCase()} as YYYY-MM-DD.`);
  if (field.type === "currency" && !/^[A-Z]{3}$/.test(value)) return issue(field.key, "invalid_currency", "Enter a three-letter currency code, such as EUR.");
  if (field.type === "amount") {
    const amount = units(value);
    if (amount === null || (field.key === "grossAmount" && amount <= 0n)) {
      return issue(field.key, "invalid_amount", `Enter ${field.key === "grossAmount" ? "a positive" : "a non-negative"} ${label.toLowerCase()} with at most four decimal places.`);
    }
  }
  if (field.key === "dueDate" && date(string(values.issuedDate).trim()) && value < string(values.issuedDate).trim()) {
    return issue(field.key, "date_order", "The due date cannot be before the issue date.");
  }
  return null;
}

function currentConfirmation({ review, sourceKey, key, value, assignment }) {
  const confirmation = object(review?.fields?.[key]);
  return review?.version === 1 && review.sourceKey === sourceKey
    && ["confirmed", "edited"].includes(confirmation.kind)
    && typeof confirmation.reviewedAt === "string" && Number.isFinite(Date.parse(confirmation.reviewedAt))
    && confirmation.value === string(value) && confirmation.assignmentKey === assignmentKey(assignment)
    ? confirmation : null;
}

// Source coordinates are raw PDF user-space rectangles. A renderer must apply
// the real PDF page viewport transform; do not turn these into guessed highlights.
export function invoiceFieldEvidence({ item, assignment } = {}) {
  const extraction = object(item?.extraction?.invoice || item?.extraction);
  const source = object(assignment), location = object(source.sourceLocation);
  const ids = new Set((Array.isArray(source.tokenIds) ? source.tokenIds : Array.isArray(location.token_ids) ? location.token_ids : []).slice(0, 1200));
  const tokens = (Array.isArray(extraction.tokens) ? extraction.tokens : []).slice(0, 1200).filter(token => ids.has(token.id));
  const sourcePage = Number(location.page || tokens[0]?.page);
  const page = Number.isSafeInteger(sourcePage) && sourcePage >= 1 && sourcePage <= 50 ? sourcePage : null;
  const rectangles = invoiceImportCoordinateVersions.includes(extraction.parserVersion) ? tokens.flatMap(token => {
    const coordinates = [token.x, token.y, token.width, token.height];
    const dimensions = extraction.pageDimensions?.[token.page];
    if (token.page !== page || !coordinates.every(Number.isFinite) || token.width <= 0 || token.height <= 0
      || !dimensions || !Number.isFinite(dimensions.width) || !Number.isFinite(dimensions.height)
      || token.x < 0 || token.y < 0 || token.x + token.width > dimensions.width || token.y + token.height > dimensions.height) return [];
    return [{ tokenId: token.id, page, coordinateSpace: "pdf-user-space", bounds: [token.x, token.y, token.x + token.width, token.y + token.height] }];
  }) : [];
  return { page, line: Number.isSafeInteger(location.line) && location.line > 0 ? location.line : null,
    text: bounded(source.sourceText || tokens.map(token => token.text).join(" "), 480),
    rectangles, hasCoordinates: rectangles.length > 0,
    unavailableReason: rectangles.length ? null : page ? "The source page is available, but reliable highlight coordinates are not." : "No PDF location is available for this value. Check the original document or enter it manually." };
}

export function invoiceEdiValidationKey({ item = {}, values = {}, exportProfile = "generic", ediPosting = {} } = {}) {
  return JSON.stringify([invoiceReviewSourceKey(item), item.revision ?? null, exportProfile,
    INVOICE_IMPORT_FIELDS.map(field => [field.key, string(values[field.key])]),
    ["profile", "treatmentId", "identifier", "vatNumber", "amountEur", "exchangeRate", "rowNumber"].map(key => [key, string(ediPosting[key])]),
    (Array.isArray(ediPosting.allocations) ? ediPosting.allocations : []).map(row => [string(row.id), string(row.treatmentId), string(row.amountEur)])]);
}

// The caller may bind a receipt only after the authorized export adapter verifies
// the frozen source snapshot against this key. Client errors=[] is not validation.
// This UI gate never replaces the server's export permissions or snapshot checks.
function currentEdiValidation(validation, key, item, profile, now) {
  const receipt = object(validation?.receipt), prepared = Date.parse(receipt.preparedAt), expires = Date.parse(receipt.expiresAt);
  return validation?.contextKey === key && validation?.snapshotVerified === true
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(receipt.id)
    && receipt.organizationId === item.workspaceId && receipt.businessId === item.businessId && receipt.profile === profile
    && receipt.status === "prepared" && receipt.sourceCount === 1 && receipt.errorCount === 0
    && Number.isSafeInteger(receipt.rowCount) && receipt.rowCount > 0 && /^[a-f0-9]{64}$/.test(receipt.manifestSha256)
    && Number.isFinite(now) && Number.isFinite(prepared) && Number.isFinite(expires)
    && prepared <= now && expires > now && expires > prepared;
}

export function invoiceReviewReadiness({ item = {}, values = {}, assignments = {}, review = null,
  exportProfile = "generic", ediPosting = {}, ediErrors = null, ediValidation = null,
  filingReviewRequired = false, invoiceSaved = false, now = Date.now() } = {}) {
  const required = new Set(requiredInvoiceImportFields(invoiceType(item.documentType)));
  const sourceKey = invoiceReviewSourceKey(item), issues = [];
  const fields = INVOICE_IMPORT_FIELDS.map(field => {
    const value = string(values[field.key]), source = object(assignments[field.key]);
    const invalid = fieldIssue(field, values, required.has(field.key), item.documentType);
    const confirmation = currentConfirmation({ review, sourceKey, key: field.key, value, assignment: source });
    const hasSource = Boolean(source.sourceText || source.tokenIds?.length || source.sourceLocation?.page);
    const manual = ["manual-entry", "manual-edit"].includes(source.method);
    const confidence = typeof source.confidence === "number" && Number.isFinite(source.confidence) ? source.confidence : null;
    const needsChecking = Boolean(value.trim() && hasSource && !manual && !confirmation && (confidence === null || confidence < 0.82 || confidence > 1));
    const attention = invalid || (needsChecking ? issue(field.key, "needs_checking", `${confidence === null || confidence > 1 ? 'Extraction confidence is unavailable.' : 'The extraction is uncertain.'} Check ${labelFor(field.key, item.documentType).toLowerCase()} against the PDF, then confirm the value or edit it.`) : null);
    if (attention) issues.push(attention);
    const status = invalid ? invalid.code === "required_missing" ? "required_missing" : "invalid"
      : !value.trim() ? "optional_blank" : confirmation ? confirmation.kind : needsChecking ? "needs_checking" : hasSource && !manual ? "from_pdf" : "entered";
    return { ...field, label: labelFor(field.key, item.documentType), required: required.has(field.key), value,
      status, issue: attention, confirmation, evidence: invoiceFieldEvidence({ item, assignment: source }) };
  });
  const gross = units(string(values.grossAmount).trim()), net = units(string(values.netAmount).trim()), tax = units(string(values.taxAmount).trim());
  if (gross !== null && tax !== null && tax > gross) issues.push(issue("taxAmount", "tax_exceeds_total", "Tax cannot exceed the invoice total. Check the amounts against the PDF."));
  else if (gross !== null && net !== null && tax !== null && net + tax !== gross) issues.push(issue("netAmount", "amount_difference", "Net amount plus tax must equal the invoice total. Check or edit the amounts."));
  const configured = ["edi-purchase", "edi-sales"].includes(exportProfile);
  const validationKey = invoiceEdiValidationKey({ item, values, exportProfile, ediPosting });
  const validationCurrent = currentEdiValidation(ediValidation, validationKey, item, exportProfile, now);
  const exportIssues = Array.isArray(ediErrors) ? ediErrors.map(message => issue("edi", "edi_validation", string(message))) : [];
  if (!validationCurrent) exportIssues.unshift(issue("edi", "validation_required", "Prepare and validate the EDI export for the current saved invoice before downloading it."));
  if (!configured) exportIssues.unshift(issue("edi", "profile_missing", "Choose and complete an EDI export profile."));
  if (configured && (invoiceType(item.documentType) === "sales_invoice") !== (exportProfile === "edi-sales")) {
    exportIssues.unshift(issue("edi", "profile_mismatch", "Choose the EDI profile that matches this invoice type."));
  }
  if (filingReviewRequired) exportIssues.unshift(issue("edi", "edi_reconfirmation", "Confirm the EDI treatment and EUR amount after the invoice changes."));
  if (!invoiceSaved) exportIssues.unshift(issue("invoice", "invoice_not_saved", "Save the invoice before exporting it."));
  return { sourceKey, fields,
    invoice: { canSave: issues.length === 0, issues, missingRequired: issues.filter(entry => entry.code === "required_missing").length,
      needsChecking: issues.filter(entry => entry.code === "needs_checking").length },
    edi: { configured, validationKey, validationCurrent, canExport: issues.length === 0 && exportIssues.length === 0, issues: exportIssues },
    showOptional: fields.some(field => !field.required && field.issue) };
}

export function confirmInvoiceReviewField({ item, values, assignments, review, key, kind = "confirmed", now = new Date().toISOString() }) {
  if (!fieldMap.has(key) || !["confirmed", "edited"].includes(kind) || !Number.isFinite(Date.parse(now))) throw new Error("Choose a valid invoice value to review.");
  const state = invoiceReviewReadiness({ item, values, assignments, review });
  const field = state.fields.find(candidate => candidate.key === key);
  if (!field.value.trim() || state.invoice.issues.some(entry => entry.field === key && entry.code !== "needs_checking")) {
    throw new Error(field.issue?.message || "Enter a valid value before confirming it.");
  }
  return { version: 1, sourceKey: state.sourceKey,
    fields: { ...(review?.version === 1 && review.sourceKey === state.sourceKey ? object(review.fields) : {}),
      [key]: { kind, value: field.value, assignmentKey: assignmentKey(assignments?.[key]), reviewedAt: now } } };
}
